import * as React from 'react';
import Grid from '@mui/material/Grid2';
import BaseSelect from '../../../../../components/BaseSelect';
import {
  IMAGES_DATASETS_BUCKET,
  LAUNCH_BUTTON,
  SOCKET_USER_UPDATE_EVENT,
} from '../../../../../constants/base';
import { observer } from 'mobx-react';
import BaseButton from '../../../../../components/BaseButton';
import BaseInput from '../../../../../components/BaseInput';
import DataStore from '../../../../../store';
import { validateForm } from '../../../../../helpers/formValidator';
import { notificationStore } from '../../../../../store/Notification';
import { MISSING_FIELDS_MESSAGE } from '../../../../../helpers/constants';
import socketStore from '../../../../../store/Socket';
import ProgressBar from '../../../../../components/ProgressBar';
import BaseDateRangePicker, {
  INVALID_DATE_RANGE,
} from '../../../../../components/BaseDateRangePicker';
import { validationRules } from '../../../../../helpers/validationRules';
import { getPageConfiguration } from '../../../../../helpers/groups';
import { DateTime } from 'luxon';
import { Box, Typography, Link } from '@mui/material';
import { useSocketListener } from '../../../../../hooks/useSocketListener';
import { ProgressPayload } from '../../../../../types/global';

const Search = observer(() => {
  const [progress, setProgress] = React.useState(0);
  const [progressMax, setProgressMax] = React.useState(0);
  const [progressMessage, setProgressMessage] = React.useState('');

  React.useEffect(() => {
    showOutput();
  }, []);
  const socket = socketStore.socket;
  const channel = React.useMemo(() => SOCKET_USER_UPDATE_EVENT(), []);
  const onProgress = React.useCallback((data: ProgressPayload): void => {
    setProgress(data.progress ?? 0);
    setProgressMax(data.total ?? 0);
    setProgressMessage(data.message ?? '');
  }, []);
  useSocketListener<ProgressPayload>(socket, channel, onProgress, true);

  const showOutput = (resData: any = {}) => {
    DataStore.showOutput(
      'Search launch',
      { ...resData, showType: 'search' },
      'tree'
    );
  };

  const normalizeSearchDateRange = (dateRange: string | null) => {
    const [rawFrom = '', rawTo = ''] = (dateRange ?? '').split('-', 2);
    const from = rawFrom.trim();
    const to = rawTo.trim();
    const today = DateTime.now().startOf('day').toFormat('yyyyLLdd');

    if (!from && !to) {
      return { value: getDateRangeValues('months', 3), error: null };
    }

    if (from && !to) {
      return { value: `${from}-${today}`, error: null };
    }

    if (!from && to) {
      return {
        value: null,
        error: 'Please select a From date when using a To date.',
      };
    }

    return { value: `${from}-${to}`, error: null };
  };

  const fieldLabels: { [key: string]: string } = {
    slideNumber: 'Slide Number',
    keyword: 'Keyword',
    bucket: 'Bucket',
    dateRange: 'Date Range',
  };

  const validationSchema = {
    ...{
      slideNumber: validationRules.slideNumber,
      keyword: validationRules.keyword,
    },
  };

  const [errors, setErrors] = React.useState({});

  const DATE_RANGE_LAST_DAY = 'last day';
  const DATE_RANGE_LAST_WEEK = 'last week';
  const DATE_RANGE_LAST_MONTH = 'last month';

  const [data, setData] = React.useState<{
    slideNumber: string;
    keyword: string;
    bucket: string;
    dateRange: string | null;
  }>({
    slideNumber: '',
    keyword: '',
    bucket: IMAGES_DATASETS_BUCKET,
    dateRange: null,
  });
  const onChangeData = (event: any) => {
    const { name, value }: any = event.target;
    setData((prevData: any) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const launchRun = async (e: any) => {
    e.preventDefault();

    if (data.dateRange === INVALID_DATE_RANGE) {
      notificationStore.showMessage('Some fields are invalid.', 'error', {
        'Date Range': 'Date Range is not in a valid format.',
      });
      return;
    }

    setProgress(0);
    setProgressMessage('Starting...');
    showOutput({});

    const normalizedDateRange = normalizeSearchDateRange(data.dateRange);

    if (normalizedDateRange.error) {
      notificationStore.showMessage('Some fields are invalid.', 'error', {
        'Date Range': normalizedDateRange.error,
      });
      return;
    }
    const payload = {
      ...data,
      dateRange: normalizedDateRange.value,
    };

    const validError = validateForm(payload, validationSchema);
    if (!validError) {
      setErrors({});
      const resData: any = await DataStore.searchLaunchRequest(payload);
      if (resData) {
        showOutput(resData);
      }
    } else {
      const isRequiredMissing = (Object.values(validError) as string[]).some(
        (msg) => msg.toLowerCase().includes('is required')
      );
      const humanizedErrors = Object.entries(validError).reduce(
        (acc: any, [key, message]) => {
          const label = fieldLabels[key] || key;
          const safeMessage =
            typeof message === 'string' ? message : String(message);
          acc[label] = safeMessage.replace(new RegExp(key, 'gi'), label);
          return acc;
        },
        {}
      );
      notificationStore.showMessage(
        isRequiredMissing ? MISSING_FIELDS_MESSAGE : 'Some fields are invalid.',
        'error',
        humanizedErrors
      );
      setErrors(humanizedErrors);
    }
  };

  const getBucketValues = () => {
    const pageConfiguration: any = getPageConfiguration('search');
    return pageConfiguration?.bucket || [];
  };

  const getDateRangeValues = (type: string = 'months', number: number = 3) => {
    const now = DateTime.now().startOf('day');
    return `${now.minus({ [type]: number }).toFormat('yyyyLLdd')}-${now.toFormat('yyyyLLdd')}`;
  };

  const applyDateOption = (option: string) => {
    let value = data.dateRange;

    switch (option) {
      case DATE_RANGE_LAST_DAY:
        value = getDateRangeValues('days', 1);
        break;
      case DATE_RANGE_LAST_WEEK:
        value = getDateRangeValues('weeks', 1);
        break;
      case DATE_RANGE_LAST_MONTH:
        value = getDateRangeValues('months', 1);
        break;
      default:
        return;
    }
    setData((prev) => ({ ...prev, dateRange: value }));
  };

  const getSelectDateOptions = () => {
    const options = [
      { label: 'day', value: DATE_RANGE_LAST_DAY },
      { label: 'week', value: DATE_RANGE_LAST_WEEK },
      { label: 'month', value: DATE_RANGE_LAST_MONTH },
    ];

    return (
      <Box display='flex' alignItems='center' justifyContent='space-between'>
        <Box display='flex' alignItems='center'>
          <Typography variant='body2' sx={{ mr: 0.5 }}>
            If not specified, last 3 months will be used.
          </Typography>
        </Box>
        <Box display='flex' alignItems='center'>
          <Typography variant='body2' sx={{ mr: 0.5 }}>
            last:
          </Typography>
          {options.map((option, index) => (
            <Box key={option.label} display='flex' alignItems='center'>
              <Link
                component='button'
                variant='body2'
                onClick={() => applyDateOption(option.value)}
                sx={{
                  fontWeight: 600,
                  textTransform: 'lowercase',
                  mx: index < options.length - 1 ? 0.5 : 0,
                }}
              >
                {option.label}
              </Link>
              {index < options.length - 1 && (
                <Typography variant='body2' sx={{ mx: 0.5 }}>
                  |
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }}>
          <BaseInput
            feature={'search'}
            label={fieldLabels.slideNumber}
            value={data.slideNumber}
            name={'slideNumber'}
            onChange={onChangeData}
            errors={errors}
            required={validationSchema?.slideNumber?.required || false}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 9 }}>
          <BaseInput
            feature={'search'}
            label={fieldLabels.keyword}
            value={data.keyword}
            name={'keyword'}
            onChange={onChangeData}
            errors={errors}
            tooltip='Search by part of the experiment folder name.'
          />
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <BaseSelect
            feature={'search'}
            name='bucket'
            label={fieldLabels.bucket}
            values={getBucketValues()}
            value={data.bucket}
            onChange={onChangeData}
            errors={errors}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <BaseDateRangePicker
            feature={'search'}
            value={data.dateRange ?? ''}
            name={'dateRange'}
            onChange={onChangeData}
            errors={errors}
            description={getSelectDateOptions()}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 10 }}>
          <BaseButton
            text={LAUNCH_BUTTON}
            variant='contained'
            color='primary'
            isDisabled={DataStore.isProcessing}
            spinner={DataStore.isProcessing}
            onClick={launchRun}
          />
        </Grid>
      </Grid>
      <ProgressBar
        actual={progress}
        max={progressMax}
        message={progressMessage}
      />
    </>
  );
});

export default Search;
